import Tabs from '../../components/AnimatedTabs';
import Table from "../../components/RecordTable";
import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Button,
  Space,
  Modal,
  Form,
  Input,
  InputNumber,
  message,
  Tag,
  Popconfirm,
  Typography,
  Switch,
  Row,
  Col,
  Spin,
  Select,

  Drawer,
  Divider,
  DatePicker,
  Statistic,
  Empty,
  Avatar,
  Descriptions,
  Image,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CrownOutlined,
  StarOutlined,
  CheckCircleOutlined,
  SafetyCertificateOutlined,
  UserOutlined,
  CalendarOutlined,
  ExperimentOutlined,
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import styled, { ThemeProvider } from "styled-components";
import theme from "../../config/theme";
import { BASEURL } from "../../config/api";
import axios from "../../services/axiosInstance";
import { userAPI, appointmentAPI, testBookingAPI, pointsSettingAPI, patientAPI, doctorSettingsAPI } from "../../services/api";
import { PageHero } from "../../components/Illustration";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const PageContainer = styled.div`
  padding: ${(props) => props.theme.spacing.xl}px;
  background: ${(props) => props.theme.colors.background};
  min-height: 100vh;
`;

const StyledCard = styled(Card)`
  border-radius: ${(props) => props.theme.borderRadius.lg}px;
  box-shadow: ${(props) => props.theme.shadows.md};
  margin-bottom: ${(props) => props.theme.spacing.xl}px;
  border: 1px solid ${(props) => props.theme.colors.borderLight};

  .ant-card-head {
    background: linear-gradient(
      135deg,
      ${(props) => props.theme.gradients.primary.join(", ")}
    );
    border-radius: ${(props) => props.theme.borderRadius.lg}px
      ${(props) => props.theme.borderRadius.lg}px 0 0;
    border: none;

    .ant-card-head-title {
      color: ${(props) => props.theme.colors.textInverse};
      font-weight: 600;
      font-size: 18px;
      display: flex;
      align-items: center;
      gap: ${(props) => props.theme.spacing.sm}px;
    }
  }
`;

const GradientButton = styled(Button)`
  background: linear-gradient(
    135deg,
    ${(props) => props.theme.gradients.primary.join(", ")}
  );
  border: none;
  color: white;
  font-weight: 600;

  &:hover {
    background: linear-gradient(
      135deg,
      ${(props) => props.theme.gradients.primary[1]},
      ${(props) => props.theme.gradients.primary[0]}
    );
    color: white;
  }
`;

const PlanCard = styled.div`
  border: 2px solid ${(props) => (props.$active ? "#0A6EBD" : "#e8e8e8")};
  border-radius: 16px;
  padding: 20px;
  background: ${(props) =>
    props.$active
      ? "linear-gradient(135deg, #F0F9FF, #ffffff)"
      : "#fafafa"};
  position: relative;
  transition: all 0.3s;
  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

// Blue shades only, so every plan stays inside the NephroCore palette.
const GRADIENT_OPTIONS = [
  { label: "Blue", value: '["#3B82F6","#1E40AF"]' },
  { label: "NephroCore blue", value: '["#0A6EBD","#0859A0"]' },
  { label: "Deep navy", value: '["#1E3A8A","#172554"]' },
  { label: "Sky", value: '["#38BDF8","#0369A1"]' },
];

// ─── Profile Verification Tab ──────────────────────────────────────────────────

const ProfileVerificationTab = () => {
  const [fee, setFee] = useState(0);
  const [feeLoading, setFeeLoading] = useState(true);
  const [savingFee, setSavingFee] = useState(false);

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [dateRange, setDateRange] = useState(null);
  const [search, setSearch] = useState("");
  const [fetched, setFetched] = useState(false);

  const [viewUser, setViewUser] = useState(null);
  const [viewPatient, setViewPatient] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [userAppointments, setUserAppointments] = useState([]);
  const [userLabTests, setUserLabTests] = useState([]);

  useEffect(() => {
    pointsSettingAPI.getProfileVerificationFee()
      .then(res => { if (res?.success) setFee(res.data.profileVerificationFee ?? 0); })
      .catch(() => {})
      .finally(() => setFeeLoading(false));
    fetchVerifiedUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveFee = async () => {
    setSavingFee(true);
    try {
      await pointsSettingAPI.updateProfileVerificationFee(fee);
      message.success("Verification fee saved successfully");
    } catch {
      message.error("Failed to save fee");
    } finally {
      setSavingFee(false);
    }
  };

  const fetchVerifiedUsers = async () => {
    setUsersLoading(true);
    try {
      // Fetch both pending and approved verification requests
      const [pendingRes, approvedRes] = await Promise.allSettled([
        userAPI.getAll({ profileVerificationStatus: "pending", role: "patient", limit: 100 }),
        userAPI.getAll({ profileVerified: "true", role: "patient", limit: 100 }),
      ]);
      const pending = pendingRes.value?.data?.users || pendingRes.value?.data?.data?.users || [];
      const approved = approvedRes.value?.data?.users || approvedRes.value?.data?.data?.users || [];
      // Merge, deduplicate by _id
      const merged = [...pending, ...approved.filter(a => !pending.some(p => p._id === a._id))];
      // Client-side filters
      let filtered = merged;
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        filtered = filtered.filter(u =>
          u.name?.toLowerCase().includes(q) ||
          u.email?.toLowerCase().includes(q) ||
          u.phone?.includes(q)
        );
      }
      if (dateRange?.[0] && dateRange?.[1]) {
        filtered = filtered.filter(u => {
          if (!u.profileVerificationPaidAt) return false;
          const d = new Date(u.profileVerificationPaidAt);
          return d >= dateRange[0].startOf("day").toDate() && d <= dateRange[1].endOf("day").toDate();
        });
      }
      setUsers(filtered);
      setFetched(true);
    } catch {
      message.error("Failed to fetch verification requests");
    } finally {
      setUsersLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    try {
      await userAPI.approveVerification(userId);
      message.success("Profile verification approved");
      setUsers(prev => prev.map(u =>
        u._id === userId
          ? { ...u, profileVerified: true, profileVerificationStatus: "approved" }
          : u
      ));
    } catch {
      message.error("Failed to approve verification");
    }
  };

  const openUserDrawer = useCallback(async (user) => {
    setViewUser(user);
    setViewPatient(null);
    setDrawerOpen(true);
    setDrawerLoading(true);
    setUserAppointments([]);
    setUserLabTests([]);
    try {
      const [apptRes, labRes, patientRes] = await Promise.allSettled([
        appointmentAPI.getAll({ patientUserId: user._id, limit: 50 }),
        testBookingAPI.getByPatient(user._id),
        patientAPI.getByUserId(user._id),
      ]);
      const appts = apptRes.value?.data?.data?.appointments || apptRes.value?.data?.appointments || [];
      const labs = labRes.value?.data?.data || labRes.value?.data?.bookings || (Array.isArray(labRes.value?.data) ? labRes.value.data : []);
      const pd = patientRes.value?.data || null;
      setUserAppointments(appts);
      setUserLabTests(labs);
      setViewPatient(pd);
    } catch {
      message.error("Failed to load user details");
    } finally {
      setDrawerLoading(false);
    }
  }, []);

  const enrollmentColumns = [
    { title: "#", key: "idx", width: 48, render: (_, __, i) => i + 1 },
    {
      title: "Patient",
      key: "patient",
      render: (_, r) => (
        <Space>
          <Avatar src={r.profileImage} icon={<UserOutlined />} style={{ background: "#0A6EBD" }} />
          <div>
            <div style={{ fontWeight: 600 }}>{r.name}</div>
            <div style={{ fontSize: 12, color: "#888" }}>{r.email}</div>
            <div style={{ fontSize: 12, color: "#888" }}>{r.phone}</div>
          </div>
        </Space>
      ),
    },
    {
      title: "Requested On",
      key: "requestedOn",
      render: (_, r) =>
        r.profileVerificationPaidAt
          ? dayjs(r.profileVerificationPaidAt).format("DD MMM YYYY HH:mm")
          : <Text type="secondary">—</Text>,
      sorter: (a, b) => new Date(a.profileVerificationPaidAt) - new Date(b.profileVerificationPaidAt),
      defaultSortOrder: "descend",
    },
    {
      title: "Verification Status",
      key: "verificationStatus",
      render: (_, r) => {
        if (r.profileVerified) return <Tag color="blue" icon={<CheckCircleOutlined />}>Approved</Tag>;
        if (r.profileVerificationStatus === "pending") return <Tag color="orange">Pending Approval</Tag>;
        return <Tag color="default">—</Tag>;
      },
      filters: [
        { text: "Pending", value: "pending" },
        { text: "Approved", value: "approved" },
      ],
      onFilter: (value, r) =>
        value === "approved" ? !!r.profileVerified : r.profileVerificationStatus === "pending" && !r.profileVerified,
    },
    {
      title: "Joined",
      key: "joined",
      render: (_, r) => dayjs(r.createdAt).format("DD MMM YYYY"),
    },
    {
      title: "Action",
      key: "action",
      render: (_, r) => (
        <Space>
          <Button icon={<EyeOutlined />} size="small" type="primary" ghost onClick={() => openUserDrawer(r)}>
            View
          </Button>
          {!r.profileVerified && r.profileVerificationStatus === "pending" && (
            <Popconfirm
              title="Approve this profile verification request?"
              onConfirm={() => handleApprove(r._id)}
              okText="Approve"
              cancelText="Cancel"
            >
              <Button icon={<CheckCircleOutlined />} size="small" type="primary" style={{ background: "#0A6EBD", borderColor: "#0A6EBD" }}>
                Approve
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const apptColumns = [
    { title: "#", key: "idx", width: 40, render: (_, __, i) => i + 1 },
    { title: "Doctor", key: "doc", render: (_, r) => r.doctor?.name || "—" },
    {
      title: "Date",
      key: "date",
      render: (_, r) => r.appointmentDate ? dayjs(r.appointmentDate).format("DD MMM YYYY") : "—",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (s) => (
        <Tag color={{ pending: "orange", confirmed: "blue", completed: "green", cancelled: "red" }[s] || "default"}>
          {s?.toUpperCase() || "—"}
        </Tag>
      ),
    },
    { title: "Fee", key: "fee", render: (_, r) => `₹${r.doctor?.fees || r.consultationFee || 0}` },
  ];

  const labColumns = [
    { title: "#", key: "idx", width: 40, render: (_, __, i) => i + 1 },
    {
      title: "Tests",
      key: "tests",
      render: (_, r) => (r.tests || []).map(t => t.name || t.testName || "Test").join(", ") || "—",
    },
    {
      title: "Date",
      key: "date",
      render: (_, r) => r.createdAt ? dayjs(r.createdAt).format("DD MMM YYYY") : "—",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (s) => (
        <Tag color={{ pending: "orange", confirmed: "blue", completed: "green", cancelled: "red" }[s] || "default"}>
          {s?.toUpperCase() || "—"}
        </Tag>
      ),
    },
    { title: "Amount", key: "amt", render: (_, r) => `₹${r.totalAmount || 0}` },
  ];

  return (
    <div>
      {/* Fee Configuration */}
      <Card
        style={{ marginBottom: 16, borderRadius: 12 }}
        title={
          <Space>
            <SafetyCertificateOutlined style={{ color: "#0A6EBD" }} />
            <span>Verification Fee</span>
          </Space>
        }
      >
        {feeLoading ? (
          <Spin />
        ) : (
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={10}>
              <div style={{ marginBottom: 6, fontWeight: 600, color: "#374151" }}>
                Fee Amount (₹) — set to 0 for free
              </div>
              <InputNumber
                min={0}
                value={fee}
                onChange={(v) => setFee(v ?? 0)}
                addonBefore="₹"
                style={{ width: "100%" }}
                placeholder="Enter fee in rupees"
              />
              <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 4 }}>
                This fee is charged when a patient verifies their 100%-complete profile
              </div>
            </Col>
            <Col xs={24} sm={6}>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSaveFee}
                loading={savingFee}
                style={{ marginTop: 24 }}
              >
                Save Fee
              </Button>
            </Col>
            <Col xs={24} sm={8}>
              <Card size="small" style={{ background: fee === 0 ? "#F0FDF4" : "#EFF6FF", border: "none" }}>
                <Statistic
                  title="Current Setting"
                  value={fee === 0 ? "FREE" : `₹${fee}`}
                  styles={{ content: { color: "#0A6EBD", fontSize: 22 } }}
                />
              </Card>
            </Col>
          </Row>
        )}
      </Card>

      {/* Enrollment History */}
      <Card
        style={{ borderRadius: 12 }}
        title={
          <Space>
            <CheckCircleOutlined style={{ color: "#0A6EBD" }} />
            <span>Verification Enrollment History</span>
          </Space>
        }
      >
        <Space wrap style={{ marginBottom: 16 }}>
          <Input.Search
            placeholder="Search by name, email, phone"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onSearch={fetchVerifiedUsers}
            style={{ width: 260 }}
            allowClear
          />
          <RangePicker
            value={dateRange}
            onChange={setDateRange}
            placeholder={["Verified From", "Verified To"]}
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={fetchVerifiedUsers}
            loading={usersLoading}
          >
            Search
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => { setDateRange(null); setSearch(""); fetchVerifiedUsers(); }}
          >
            Reset
          </Button>
        </Space>

        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="Total Requests"
                  value={users.length}
                  prefix={<SafetyCertificateOutlined />}
                  styles={{ content: { color: "#0A6EBD" } }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="Pending"
                  value={users.filter(u => !u.profileVerified && u.profileVerificationStatus === "pending").length}
                  styles={{ content: { color: "#0A6EBD" } }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="Approved"
                  value={users.filter(u => u.profileVerified).length}
                  styles={{ content: { color: "#0A6EBD" } }}
                />
              </Card>
            </Col>
          </Row>
          <Table
            columns={enrollmentColumns}
            dataSource={users}
            rowKey="_id"
            loading={usersLoading}
            pagination={{ pageSize: 10, showSizeChanger: false }}
            scroll={{ x: "max-content" }}
            locale={{ emptyText: <Empty description="No verification requests found" /> }}
          />
      </Card>

      {/* User Detail Drawer */}
      <Drawer
        title={
          viewUser ? (
            <Space>
              <Avatar src={viewUser.profileImage} icon={<UserOutlined />} size={36} />
              <span style={{ fontWeight: 700 }}>{viewUser.name}</span>
              {viewUser.profileVerified
                ? <Tag color="blue" icon={<CheckCircleOutlined />}>Approved</Tag>
                : <Tag color="orange">Pending Approval</Tag>}
            </Space>
          ) : "User Details"
        }
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setViewPatient(null); }}
        width={860}
      >
        {drawerLoading ? (
          <div style={{ textAlign: "center", padding: 60 }}>
            <Spin size="large" tip="Loading profile..." />
          </div>
        ) : viewUser ? (
          <>
            {/* ── Personal Information ── */}
            <Descriptions title="Personal Information" bordered size="small" column={2} style={{ marginBottom: 24 }}>
              <Descriptions.Item label="Full Name">{viewUser.name || "—"}</Descriptions.Item>
              <Descriptions.Item label="Email">{viewUser.email || "—"}</Descriptions.Item>
              <Descriptions.Item label="Phone">{viewUser.phone || "—"}</Descriptions.Item>
              <Descriptions.Item label="Gender" style={{ textTransform: "capitalize" }}>{viewUser.gender || "—"}</Descriptions.Item>
              <Descriptions.Item label="Date of Birth">
                {viewUser.dateOfBirth ? dayjs(viewUser.dateOfBirth).format("DD MMM YYYY") : "—"}
              </Descriptions.Item>
              <Descriptions.Item label="State">{viewUser.state || "—"}</Descriptions.Item>
              <Descriptions.Item label="District">{viewUser.district || "—"}</Descriptions.Item>
              <Descriptions.Item label="Pincode">{viewUser.pincode || "—"}</Descriptions.Item>
              <Descriptions.Item label="Doctor Name">{viewUser.doctorName || "—"}</Descriptions.Item>
              <Descriptions.Item label="Hospital">{viewUser.hospitalName || "—"}</Descriptions.Item>
              <Descriptions.Item label="Categories" span={2}>
                {(viewUser.categories || []).length > 0
                  ? viewUser.categories.map((c, i) => <Tag key={i}>{c}</Tag>)
                  : "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Requested On">
                {viewUser.profileVerificationPaidAt ? dayjs(viewUser.profileVerificationPaidAt).format("DD MMM YYYY HH:mm") : "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Joined">
                {dayjs(viewUser.createdAt).format("DD MMM YYYY")}
              </Descriptions.Item>
            </Descriptions>

            {/* ── Aadhar Card ── */}
            <Divider orientation="left" style={{ fontWeight: 700 }}>🪪 Aadhar Card</Divider>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              {["aadharFront", "aadharBack"].map((side) => {
                const doc = viewPatient?.documents?.[side]?.[0];
                return (
                  <Col span={12} key={side}>
                    <div style={{ fontWeight: 600, marginBottom: 8, color: "#374151" }}>
                      {side === "aadharFront" ? "Front Side" : "Back Side"}
                    </div>
                    {doc?.fileUrl ? (
                      <Image
                        src={doc.fileUrl}
                        alt={side}
                        style={{ width: "100%", maxHeight: 180, objectFit: "cover", borderRadius: 8, border: "1px solid #e5e7eb" }}
                        preview={{ mask: "Click to preview" }}
                      />
                    ) : (
                      <div style={{ height: 120, background: "#f9fafb", borderRadius: 8, border: "1px dashed #d1d5db", display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af" }}>
                        Not uploaded
                      </div>
                    )}
                  </Col>
                );
              })}
            </Row>

            {/* ── Medical Information ── */}
            <Divider orientation="left" style={{ fontWeight: 700 }}>🩺 Medical Information</Divider>
            <Descriptions bordered size="small" column={2} style={{ marginBottom: 24 }}>
              <Descriptions.Item label="Current Condition" span={2}>
                {viewPatient?.medicalInfo?.currentCondition || <Text type="secondary">—</Text>}
              </Descriptions.Item>
              <Descriptions.Item label="Duration">
                {(viewPatient?.medicalInfo?.duration?.years || viewPatient?.medicalInfo?.duration?.months)
                  ? `${viewPatient.medicalInfo.duration.years || 0} yr ${viewPatient.medicalInfo.duration.months || 0} mo`
                  : <Text type="secondary">—</Text>}
              </Descriptions.Item>
              <Descriptions.Item label="Symptoms">
                {viewPatient?.medicalInfo?.symptoms || <Text type="secondary">—</Text>}
              </Descriptions.Item>
            </Descriptions>

            {/* ── Health Details ── */}
            <Divider orientation="left" style={{ fontWeight: 700 }}>❤️ Health Details</Divider>
            <Descriptions bordered size="small" column={2} style={{ marginBottom: 24 }}>
              <Descriptions.Item label="Allergies">
                {viewPatient?.healthDetails?.allergies || <Text type="secondary">—</Text>}
              </Descriptions.Item>
              <Descriptions.Item label="Current Medications">
                {viewPatient?.healthDetails?.currentMedications || <Text type="secondary">—</Text>}
              </Descriptions.Item>
            </Descriptions>

            {/* ── Documents ── */}
            {[
              { key: "testReports",  label: "🧪 Test Reports" },
              { key: "prescriptions", label: "📋 Prescriptions" },
              { key: "bodyProof",    label: "📷 Visual Evidence" },
            ].map(({ key, label }) => {
              const docs = viewPatient?.documents?.[key] || [];
              return (
                <React.Fragment key={key}>
                  <Divider orientation="left" style={{ fontWeight: 700 }}>{label} ({docs.length})</Divider>
                  {docs.length > 0 ? (
                    <Image.PreviewGroup>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 24 }}>
                        {docs.map((doc) => (
                          <div key={doc._id} style={{ position: "relative" }}>
                            <Image
                              src={doc.fileUrl}
                              alt={doc.fileName}
                              width={120}
                              height={100}
                              style={{ objectFit: "cover", borderRadius: 8, border: "1px solid #e5e7eb" }}
                              preview={{ mask: "Preview" }}
                            />
                            <div style={{ fontSize: 10, color: "#6b7280", marginTop: 4, maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {doc.fileName}
                            </div>
                          </div>
                        ))}
                      </div>
                    </Image.PreviewGroup>
                  ) : (
                    <div style={{ color: "#9ca3af", marginBottom: 24, fontStyle: "italic" }}>No files uploaded</div>
                  )}
                </React.Fragment>
              );
            })}

            {/* ── Appointments ── */}
            <Divider orientation="left" style={{ fontWeight: 700 }}>
              <Space><CalendarOutlined />Appointments ({userAppointments.length})</Space>
            </Divider>
            <Table
              columns={apptColumns}
              dataSource={userAppointments}
              rowKey="_id"
              size="small"
              pagination={{ pageSize: 5, showSizeChanger: false }}
              locale={{ emptyText: <Empty description="No appointments" /> }}
              style={{ marginBottom: 24 }}
            />

            {/* ── Lab Tests ── */}
            <Divider orientation="left" style={{ fontWeight: 700 }}>
              <Space><ExperimentOutlined />Lab Test Bookings ({userLabTests.length})</Space>
            </Divider>
            <Table
              columns={labColumns}
              dataSource={userLabTests}
              rowKey="_id"
              size="small"
              pagination={{ pageSize: 5, showSizeChanger: false }}
              locale={{ emptyText: <Empty description="No lab test bookings" /> }}
            />
          </>
        ) : null}
      </Drawer>
    </div>
  );
};

// ─── Membership Plans Settings Page ───────────────────────────────────────────

const MembershipSettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [plans, setPlans] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [benefitInput, setBenefitInput] = useState("");
  const [benefits, setBenefits] = useState([]);
  const [patientCategories, setPatientCategories] = useState([]);
  const [form] = Form.useForm();

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BASEURL}/membership/admin/plans`);
      if (res.success) {
        setPlans(res.data);
      }
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to fetch plans");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
    doctorSettingsAPI
      .getSettings()
      .then((res) => {
        setPatientCategories(res?.data?.data?.categories || res?.data?.categories || []);
      })
      .catch(() => {});
  }, []);

  const showModal = (plan = null) => {
    setEditingPlan(plan);
    if (plan) {
      form.setFieldsValue({
        name: plan.name,
        price: plan.price,
        duration: plan.duration,
        durationDays: plan.durationDays,
        popular: plan.popular,
        isActive: plan.isActive,
        sortOrder: plan.sortOrder,
        discountPercent: plan.discountPercent || 0,
        eligibleCategories: plan.eligibleCategories || [],
        gradient: JSON.stringify(plan.gradient),
      });
      setBenefits(plan.benefits || []);
    } else {
      form.resetFields();
      form.setFieldsValue({
        duration: "month",
        durationDays: 30,
        popular: false,
        isActive: true,
        discountPercent: 0,
        eligibleCategories: [],
        sortOrder: plans.length + 1,
        gradient: '["#3B82F6","#1E40AF"]',
      });
      setBenefits([]);
    }
    setIsModalVisible(true);
  };

  const handleSubmit = async (values) => {
    if (benefits.length === 0) {
      message.error("Please add at least one benefit");
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        ...values,
        gradient: JSON.parse(values.gradient || '["#3B82F6","#1E40AF"]'),
        benefits,
      };

      if (editingPlan) {
        await axios.put(
          `${BASEURL}/membership/plans/${editingPlan._id}`,
          payload
        );
        message.success("Plan updated successfully!");
      } else {
        await axios.post(`${BASEURL}/membership/plans`, payload);
        message.success("Plan created successfully!");
      }

      setIsModalVisible(false);
      form.resetFields();
      setEditingPlan(null);
      setBenefits([]);
      fetchPlans();
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to save plan");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (planId) => {
    try {
      setSubmitting(true);
      await axios.delete(`${BASEURL}/membership/plans/${planId}`);
      message.success("Plan deleted successfully!");
      fetchPlans();
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to delete plan");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (plan) => {
    try {
      await axios.put(
        `${BASEURL}/membership/plans/${plan._id}`,
        { isActive: !plan.isActive }
      );
      message.success(`Plan ${!plan.isActive ? "activated" : "deactivated"}!`);
      fetchPlans();
    } catch (err) {
      message.error("Failed to update plan status");
    }
  };

  const addBenefit = () => {
    if (benefitInput.trim()) {
      setBenefits([...benefits, benefitInput.trim()]);
      setBenefitInput("");
    }
  };

  const removeBenefit = (idx) => {
    setBenefits(benefits.filter((_, i) => i !== idx));
  };

  const columns = [
    {
      title: "Plan",
      key: "plan",
      render: (_, record) => (
        <Space>
          <div
            style={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              background: record.gradient?.[0] || "#3B82F6",
            }}
          />
          <Text strong>{record.name}</Text>
          {record.popular && (
            <Tag color="blue" icon={<StarOutlined />}>
              Popular
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      render: (price, record) => (
        <Text strong style={{ color: theme.colors.primary, fontSize: 16 }}>
          ₹{price}/{record.duration}
        </Text>
      ),
    },
    {
      title: "Duration",
      dataIndex: "durationDays",
      key: "durationDays",
      render: (days, record) =>
        record.duration === "lifetime" ? <Tag color="gold">Lifetime</Tag> : <Tag>{days} days</Tag>,
    },
    {
      title: "Discount",
      dataIndex: "discountPercent",
      key: "discountPercent",
      render: (pct) => (pct ? <Tag color="blue">{pct}%</Tag> : <Tag>None</Tag>),
    },
    {
      title: "Eligible For",
      dataIndex: "eligibleCategories",
      key: "eligibleCategories",
      render: (cats) =>
        cats?.length ? cats.map((c) => <Tag key={c}>{c}</Tag>) : <Tag>Any category</Tag>,
    },
    {
      title: "Benefits",
      dataIndex: "benefits",
      key: "benefits",
      render: (benefits) => (
        <Text type="secondary">{benefits?.length || 0} benefits</Text>
      ),
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => (
        <Switch
          checked={record.isActive}
          onChange={() => handleToggleActive(record)}
          checkedChildren="Active"
          unCheckedChildren="Hidden"
        />
      ),
    },
    {
      title: "Sort Order",
      dataIndex: "sortOrder",
      key: "sortOrder",
      render: (v) => <Tag>#{v}</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => showModal(record)}
            style={{ color: theme.colors.primary }}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete Plan"
            description="Are you sure you want to delete this plan?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <LoadingContainer>
          <Spin size="large" tip="Loading membership plans..." />
        </LoadingContainer>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <PageContainer>
        <PageHero title="Membership plans" subtitle="Plans patients can join, their benefits and the profile verification fee." scene="membership" />

        <Tabs
          defaultActiveKey="plans"
          size="large"
          style={{ marginBottom: 0 }}
          items={[
            {
              key: "plans",
              label: <span><CrownOutlined /> Membership Plans</span>,
              children: (
                <div>
        {/* Preview Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          {plans.map((plan) => (
            <Col xs={24} sm={12} md={8} key={plan._id}>
              <PlanCard $active={plan.isActive}>
                {plan.popular && (
                  <Tag
                    color="blue"
                    style={{ position: "absolute", top: 12, right: 12 }}
                    icon={<StarOutlined />}
                  >
                    Most Popular
                  </Tag>
                )}
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: 4,
                      background: `linear-gradient(135deg, ${plan.gradient?.[0]}, ${plan.gradient?.[1]})`,
                    }}
                  />
                  <Text strong style={{ fontSize: 18 }}>
                    {plan.name}
                  </Text>
                </div>
                <Text style={{ fontSize: 24, fontWeight: 700, color: "#0A6EBD" }}>
                  ₹{plan.price}
                </Text>
                <Text type="secondary">/{plan.duration} · {plan.durationDays} days</Text>
                <div style={{ marginTop: 8 }}>
                  {plan.benefits?.slice(0, 3).map((b, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 6, marginBottom: 4 }}>
                      <CheckCircleOutlined style={{ color: "#0A6EBD", marginTop: 2 }} />
                      <Text style={{ fontSize: 12 }}>{b}</Text>
                    </div>
                  ))}
                  {plan.benefits?.length > 3 && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      +{plan.benefits.length - 3} more...
                    </Text>
                  )}
                </div>
                {!plan.isActive && (
                  <Tag color="red" style={{ marginTop: 8 }}>Hidden from App</Tag>
                )}
              </PlanCard>
            </Col>
          ))}
          {plans.length === 0 && (
            <Col span={24}>
              <div style={{ textAlign: "center", padding: "40px 0", color: "#999" }}>
                No plans yet. Click "Add Plan" to create membership plans.
              </div>
            </Col>
          )}
        </Row>

        {/* Plans Table */}
        <StyledCard
          title={
            <>
              <CrownOutlined /> Membership Plans Management
            </>
          }
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => showModal()}
              style={{ backgroundColor: theme.colors.secondary }}
            >
              Add Plan
            </Button>
          }
        >
          <Table
            columns={columns}
            dataSource={plans}
            rowKey="_id"
            pagination={false}
            loading={submitting}
            locale={{ emptyText: 'No plans found. Click "Add Plan" to create one.' }}
          />
        </StyledCard>

        {/* Add / Edit Modal */}
        <Modal
          title={editingPlan ? "Edit Membership Plan" : "Add Membership Plan"}
          open={isModalVisible}
          onCancel={() => {
            setIsModalVisible(false);
            form.resetFields();
            setEditingPlan(null);
            setBenefits([]);
          }}
          footer={null}
          width={640}
          zIndex={1050}
        >
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Plan Name" name="name" rules={[{ required: true, message: "Required" }]}>
                  <Input placeholder="e.g. Basic, Premium, Elite" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Price (₹)" name="price" rules={[{ required: true, message: "Required" }]}>
                  <InputNumber style={{ width: "100%" }} min={0} placeholder="e.g. 999" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Duration Label" name="duration">
                  <Select>
                    <Select.Option value="month">month</Select.Option>
                    <Select.Option value="year">year</Select.Option>
                    <Select.Option value="lifetime">lifetime (one-time payment)</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Duration (days)"
                  name="durationDays"
                  extra="Ignored for lifetime plans"
                >
                  <InputNumber style={{ width: "100%" }} min={1} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Discount (%)"
                  name="discountPercent"
                  extra="The discount actually applied to bills. Benefits text below is display only."
                >
                  <InputNumber style={{ width: "100%" }} min={0} max={100} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Eligible Patient Categories"
                  name="eligibleCategories"
                  extra="Leave empty to allow every category"
                >
                  <Select mode="tags" allowClear placeholder="Any category">
                    {patientCategories.map((c) => (
                      <Select.Option key={c} value={c}>{c}</Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Color Theme" name="gradient">
                  <Select>
                    {GRADIENT_OPTIONS.map((g) => (
                      <Select.Option key={g.value} value={g.value}>
                        <Space>
                          <div
                            style={{
                              width: 14,
                              height: 14,
                              borderRadius: 3,
                              background: `linear-gradient(135deg, ${JSON.parse(g.value).join(", ")})`,
                              display: "inline-block",
                            }}
                          />
                          {g.label}
                        </Space>
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Sort Order" name="sortOrder">
                  <InputNumber style={{ width: "100%" }} min={1} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Mark as Popular" name="popular" valuePropName="checked">
                  <Switch checkedChildren="Yes" unCheckedChildren="No" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Active (visible in app)" name="isActive" valuePropName="checked">
                  <Switch checkedChildren="Active" unCheckedChildren="Hidden" />
                </Form.Item>
              </Col>
            </Row>

            {/* Benefits */}
            <Form.Item label="Benefits">
              <Space.Compact style={{ width: "100%", marginBottom: 8 }}>
                <Input
                  value={benefitInput}
                  onChange={(e) => setBenefitInput(e.target.value)}
                  placeholder="Type a benefit and click Add"
                  onPressEnter={(e) => {
                    e.preventDefault();
                    addBenefit();
                  }}
                />
                <Button type="primary" onClick={addBenefit} icon={<PlusOutlined />}>
                  Add
                </Button>
              </Space.Compact>
              <div>
                {benefits.map((b, idx) => (
                  <Tag
                    key={idx}
                    closable
                    onClose={() => removeBenefit(idx)}
                    style={{ marginBottom: 6, padding: "4px 10px", fontSize: 13 }}
                  >
                    <CheckCircleOutlined style={{ color: "#0A6EBD", marginRight: 4 }} />
                    {b}
                  </Tag>
                ))}
                {benefits.length === 0 && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    No benefits added yet
                  </Text>
                )}
              </div>
            </Form.Item>

            <Form.Item>
              <Space style={{ width: "100%", justifyContent: "flex-end" }}>
                <Button
                  onClick={() => {
                    setIsModalVisible(false);
                    form.resetFields();
                    setEditingPlan(null);
                    setBenefits([]);
                  }}
                >
                  Cancel
                </Button>
                <GradientButton htmlType="submit" loading={submitting}>
                  {editingPlan ? "Update Plan" : "Create Plan"}
                </GradientButton>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
                </div>
              ),
            },
            {
              key: "verification",
              label: <span><SafetyCertificateOutlined /> Profile Verification</span>,
              children: <ProfileVerificationTab />,
            },
          ]}
        />
      </PageContainer>
    </ThemeProvider>
  );
};

export default MembershipSettingsPage;
